import { useEffect, useMemo, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import {
  Boxes,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'

interface FleetMachineRef {
  slug: string
  name: string
  control_mode?: string
}

interface InstanceRow {
  id: string
  name: string
  instance_name: string
  organization: { id: string | null; name: string | null; slug: string | null }
  fleet_machine: FleetMachineRef | null
  provisioning_status: string | null
  created_at: string | null
}

type Tone =
  | 'info'
  | 'success'
  | 'warning'
  | 'critical'
  | 'attention'
  | 'new'
  | 'purple'
  | 'neutral'

interface MigrationTarget {
  version: string
  label: string
  provider: string
  plugin_id: string
}

interface VersionSummary {
  id: string
  version_label: string | null
  source_label: string
  source_family: string
  migration_status: string
  migration_label: string
  migration_tone: Tone
  migration_required: boolean | null
  bootstrap_applied: boolean
  target_version: string
  error?: string
}

interface Filters {
  search: string
  fleet_machine_slug: string
  organization_slug: string
  provisioning_status: string
  control_mode: string
  order_by: string
}

interface Props {
  instances: InstanceRow[]
  pagination: { page: number; totalPages: number; total: number }
  filters: Filters
  fleet_machines: { slug: string; name: string }[]
  organizations: { slug: string; name: string }[]
  provisioning_statuses: string[]
  control_modes: string[]
  migration_target: MigrationTarget
}

const PROVISIONING_LABELS: Record<string, string> = {
  pending: 'Pendente',
  provisioning: 'Provisionando',
  ready: 'Online',
  failed: 'Falha',
}

const PILL_TONE_CLASSES: Record<Tone, string> = {
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  success: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  critical: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  attention: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
  new: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400',
  neutral: 'bg-accent text-muted-foreground',
}

function Pill({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PILL_TONE_CLASSES[tone]}`}
    >
      {children}
    </span>
  )
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

const SORT_GROUPS = {
  name: { asc: 'name_asc', desc: 'name_desc' },
  created: { asc: 'oldest', desc: 'recent' },
} as const

type SortKey = keyof typeof SORT_GROUPS

function Index({
  instances,
  pagination,
  filters,
  fleet_machines,
  organizations,
  provisioning_statuses,
  control_modes,
  migration_target,
}: Props) {
  const [query, setQuery] = useState(filters.search)
  const [versionSummaries, setVersionSummaries] = useState<Record<string, VersionSummary>>({})
  const [versionsLoading, setVersionsLoading] = useState(false)
  const instanceIds = useMemo(() => instances.map((row) => row.id).join(','), [instances])

  useEffect(() => setQuery(filters.search), [filters.search])

  useEffect(() => {
    if (!instanceIds) {
      setVersionSummaries({})
      return
    }

    const controller = new AbortController()
    const params = new URLSearchParams()
    instanceIds.split(',').forEach((id) => params.append('ids[]', id))
    setVersionsLoading(true)

    fetch(`/cc/instances/version_summaries?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.ok) setVersionSummaries(data.summaries || {})
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.error('version_summaries failed', error)
        }
      })
      .finally(() => setVersionsLoading(false))

    return () => controller.abort()
  }, [instanceIds])

  const applyFilters = (next: Partial<Filters>) => {
    router.get('/cc/instances', { ...filters, ...next, page: 1 }, { preserveState: true, preserveScroll: true })
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters({ search: query })
  }

  const toggleSort = (key: SortKey) => {
    const group = SORT_GROUPS[key]
    const next = filters.order_by === group.asc ? group.desc : group.asc
    applyFilters({ order_by: next })
  }

  const sortStateOf = (key: SortKey): 'asc' | 'desc' | null => {
    const group = SORT_GROUPS[key]
    if (filters.order_by === group.asc) return 'asc'
    if (filters.order_by === group.desc) return 'desc'
    return null
  }

  const resetFilters = () => {
    router.get(
      '/cc/instances',
      {
        search: '',
        fleet_machine_slug: '',
        organization_slug: '',
        provisioning_status: '',
        control_mode: '',
        order_by: 'name_asc',
      },
      { preserveState: true, preserveScroll: true },
    )
  }

  const goToPage = (page: number) =>
    router.get('/cc/instances', { ...filters, page }, { preserveState: true, preserveScroll: true })

  const activeFilters: { label: string; key: keyof Filters; value: string }[] = []
  if (filters.organization_slug) {
    const org = organizations.find((o) => o.slug === filters.organization_slug)
    activeFilters.push({ label: `org: ${org?.name || filters.organization_slug}`, key: 'organization_slug', value: filters.organization_slug })
  }
  if (filters.fleet_machine_slug) {
    const m = fleet_machines.find((m) => m.slug === filters.fleet_machine_slug)
    activeFilters.push({ label: `máquina: ${m?.name || filters.fleet_machine_slug}`, key: 'fleet_machine_slug', value: filters.fleet_machine_slug })
  }
  if (filters.provisioning_status) {
    activeFilters.push({ label: `status: ${PROVISIONING_LABELS[filters.provisioning_status] || filters.provisioning_status}`, key: 'provisioning_status', value: filters.provisioning_status })
  }
  if (filters.control_mode) {
    activeFilters.push({ label: `modo: ${filters.control_mode === 'fleet_api' ? 'Fleet API' : 'Legacy SSH'}`, key: 'control_mode', value: filters.control_mode })
  }
  if (filters.search) {
    activeFilters.push({ label: `busca: "${filters.search}"`, key: 'search', value: filters.search })
  }

  const hasResults = instances.length > 0
  const hasAnyFilter = activeFilters.length > 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Boxes className="h-6 w-6 text-foreground" />
          <h2 className="text-2xl font-bold text-foreground">Instâncias</h2>
          <span className="text-sm text-muted-foreground">({pagination.total})</span>
          <span className="text-xs text-muted-foreground">
            · target <code className="font-mono text-foreground">{migration_target.version}</code>
          </span>
        </div>

        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-wrap">
          <select
            value={filters.organization_slug}
            onChange={(e) => applyFilters({ organization_slug: e.target.value })}
            className="py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring max-w-[160px]"
            aria-label="Organização"
          >
            <option value="">Todas orgs</option>
            {organizations.map((o) => (
              <option key={o.slug} value={o.slug}>{o.name}</option>
            ))}
          </select>

          <select
            value={filters.fleet_machine_slug}
            onChange={(e) => applyFilters({ fleet_machine_slug: e.target.value })}
            className="py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring max-w-[160px]"
            aria-label="Máquina"
          >
            <option value="">Todas máquinas</option>
            {fleet_machines.map((m) => (
              <option key={m.slug} value={m.slug}>{m.name}</option>
            ))}
          </select>

          <select
            value={filters.provisioning_status}
            onChange={(e) => applyFilters({ provisioning_status: e.target.value })}
            className="py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring max-w-[140px]"
            aria-label="Status"
          >
            <option value="">Todos status</option>
            {provisioning_statuses.map((s) => (
              <option key={s} value={s}>{PROVISIONING_LABELS[s] || s}</option>
            ))}
          </select>

          <select
            value={filters.control_mode}
            onChange={(e) => applyFilters({ control_mode: e.target.value })}
            className="py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring max-w-[140px]"
            aria-label="Modo de controle"
          >
            <option value="">Todos modos</option>
            {control_modes.map((m) => (
              <option key={m} value={m}>{m === 'fleet_api' ? 'Fleet API' : 'Legacy SSH'}</option>
            ))}
          </select>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar nome, instância, org..."
              className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-72"
            />
          </div>

          <Button type="submit" variant="outline" size="sm">Buscar</Button>
        </form>
      </div>

      {(hasAnyFilter || versionsLoading) && (
        <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground flex-wrap">
          {versionsLoading && (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
              Lendo versões dos brokers…
            </span>
          )}
          {hasAnyFilter && activeFilters.map((f) => (
            <button
              key={`${f.key}:${f.value}`}
              type="button"
              onClick={() => applyFilters({ [f.key]: '' } as Partial<Filters>)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent hover:bg-accent/70 text-foreground"
            >
              {f.label}
              <XCircle className="h-3 w-3" />
            </button>
          ))}
          {hasAnyFilter && (
            <button type="button" onClick={resetFilters} className="text-muted-foreground hover:text-foreground underline">
              limpar tudo
            </button>
          )}
        </div>
      )}

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-240px)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-muted border-b border-border">
                <SortableHeader
                  label="Nome / Instância"
                  state={sortStateOf('name')}
                  onClick={() => toggleSort('name')}
                />
                <Th>Organização</Th>
                <Th>Máquina</Th>
                <Th>Versão</Th>
                <SortableHeader
                  label="Criada"
                  state={sortStateOf('created')}
                  onClick={() => toggleSort('created')}
                  align="right"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {instances.map((row) => (
                <tr key={row.id} className="hover:bg-accent transition-colors">
                  <td className="px-4 py-3">
                    <Link href={`/cc/instances/${row.id}`} className="flex items-center gap-3 hover:underline">
                      <StatusDot status={row.provisioning_status} />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground leading-tight">{row.name || row.instance_name}</span>
                        <span className="block font-mono text-xs text-muted-foreground leading-tight mt-0.5">{row.instance_name}</span>
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {row.organization.name || row.organization.slug || <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-foreground">{row.fleet_machine?.name || <span className="text-muted-foreground">—</span>}</div>
                    {row.fleet_machine?.control_mode && (
                      <div className="text-xs text-muted-foreground">
                        {row.fleet_machine.control_mode === 'fleet_api' ? 'Fleet API' : 'Legacy SSH'}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <VersionCell row={row} summary={versionSummaries[row.id]} loading={versionsLoading} />
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(row.created_at)}
                  </td>
                </tr>
              ))}
              {!hasResults && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                      <Boxes className="h-8 w-8 opacity-40" />
                      {hasAnyFilter
                        ? <>Nenhuma instância corresponde aos filtros. <button onClick={resetFilters} className="underline">Limpar filtros</button>.</>
                        : 'Nenhuma instância cadastrada ainda.'}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
            <span className="text-sm text-muted-foreground">
              Página {pagination.page} de {pagination.totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(pagination.page - 1)}
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => goToPage(pagination.page + 1)}
                aria-label="Próxima página"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  const alignClass = align === 'right' ? 'text-right' : 'text-left'
  return (
    <th className={`${alignClass} px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider`}>
      {children}
    </th>
  )
}

function SortableHeader({
  label,
  state,
  onClick,
  align = 'left',
}: {
  label: string
  state: 'asc' | 'desc' | null
  onClick: () => void
  align?: 'left' | 'right'
}) {
  const alignClass = align === 'right' ? 'justify-end text-right' : 'justify-start text-left'
  const Icon = state === 'asc' ? ChevronUp : state === 'desc' ? ChevronDown : ChevronsUpDown
  const iconClass = state ? 'opacity-80' : 'opacity-40'
  return (
    <th className={`px-4 py-3 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider ${state ? 'text-foreground' : 'text-muted-foreground'} hover:text-foreground transition-colors ${alignClass}`}
      >
        {label}
        <Icon className={`h-3 w-3 ${iconClass}`} />
      </button>
    </th>
  )
}

const STATUS_DOT_CLASSES: Record<string, string> = {
  ready: 'bg-green-500',
  provisioning: 'bg-blue-500 animate-pulse',
  pending: 'bg-blue-400',
  failed: 'bg-red-500',
}

function StatusDot({ status }: { status: string | null }) {
  const dot = status && STATUS_DOT_CLASSES[status] ? STATUS_DOT_CLASSES[status] : 'bg-muted-foreground/40'
  const title = status ? (PROVISIONING_LABELS[status] || status) : 'sem status'
  return (
    <span
      title={title}
      aria-label={title}
      className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${dot}`}
    />
  )
}

function VersionCell({
  row,
  summary,
  loading,
}: {
  row: InstanceRow
  summary?: VersionSummary
  loading: boolean
}) {
  if (row.fleet_machine?.control_mode !== 'fleet_api') {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  if (!summary) {
    return <span className="text-xs text-muted-foreground/60">{loading ? '…' : '—'}</span>
  }
  if (summary.error || !summary.version_label) {
    return <span className="text-xs text-muted-foreground" title={summary.error || undefined}>—</span>
  }

  const onTarget =
    summary.bootstrap_applied ||
    summary.migration_status === 'target_current' ||
    summary.source_family === 'target'
  const isLegacy =
    !onTarget && (summary.migration_required === true || summary.source_family === 'legacy')

  return (
    <div className="inline-flex items-center gap-2">
      <span className="font-mono text-xs text-muted-foreground" title={summary.source_label}>
        {summary.version_label}
      </span>
      {onTarget && <Pill tone="success">Atual</Pill>}
      {isLegacy && <Pill tone="warning">Legacy</Pill>}
    </div>
  )
}

Index.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default Index
